export interface UpgradedTemporalEntry {
    id: string;
    target_ms: number;
    initial_rendered_frame: number;
    popup_type: string;
    sequence_index: number;
    is_authoritative: boolean;
    value: string;
}

export interface UpgradedTemporalPayload {
    schema_version: number;
    entries: UpgradedTemporalEntry[];
}

/**
 * Safely parses and upgrades questionnaire attribute values from legacy V1
 * (frame-keyed objects e.g., {"431": "Neutral"}) to the robust Schema V2.
 */
export function parseAndUpgradeAttribute(
    val: string,
    startFrame: number,
    fpsNum: number,
    fpsDen: number,
    hasStableMetadata: boolean
): UpgradedTemporalPayload {
    const trimmed = (val || '').trim();
    if (!trimmed) {
        return { schema_version: 2, entries: [] };
    }

    try {
        const parsed = JSON.parse(trimmed);

        // Standard Schema V2 format
        if (parsed && parsed.schema_version === 2) {
            if (!Array.isArray(parsed.entries)) {
                throw new Error('Schema version 2 must contain an entries array.');
            }
            return parsed;
        }

        // Legacy V1 format: object with frame keys and string values e.g., {"431": "Neutral"}
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
            const entries: UpgradedTemporalEntry[] = [];

            for (const [frameKey, valueStr] of Object.entries(parsed)) {
                const frameNumber = parseInt(frameKey, 10);
                if (Number.isNaN(frameNumber)) {
                    throw new Error(`Invalid legacy frame: ${frameKey}`);
                }

                // Compute precise ms offset using Rational Integer Math:
                // T_ms = floor((frame - startFrame) * fpsDen * 1000 / fpsNum)
                const relativeFrame = Math.max(0, frameNumber - startFrame);
                const targetMs = Math.floor((relativeFrame * fpsDen * 1000) / fpsNum);

                entries.push({
                    id: `legacy_${frameNumber}`,
                    target_ms: targetMs,
                    initial_rendered_frame: frameNumber,
                    popup_type: 'phase2b.popup.recurring',
                    sequence_index: 0,
                    // Mark as authoritative ONLY if verified stable timing metadata exists:
                    is_authoritative: hasStableMetadata,
                    value: String(valueStr),
                });
            }

            return { schema_version: 2, entries };
        }

        throw new Error('Malformed or corrupted payload format');
    } catch (e) {
        // Fail-Closed Sentinel: return schema_version = -1 to halt submission and show warning modal
        return { schema_version: -1, entries: [] };
    }
}

/**
 * Serializes the structural UpgradedTemporalPayload back to JSON string for saving in attribute values.
 */
export function serializeAttribute(entries: UpgradedTemporalEntry[]): string {
    const payload: UpgradedTemporalPayload = {
        schema_version: 2,
        entries,
    };
    return JSON.stringify(payload);
}
